// Rechner: Einzelnes Bild auf gewünschter Höhe aufhängen. Interaktive Drag-Vorschau.
import React, { useEffect, useMemo, useState } from "react";
import { calcSinglePicture, fmtCm } from "@/lib/calc";
import { track, trackSettled } from "@/lib/track";
import EditorShell from "./EditorShell";
import { startDrag, round1, clamp, fmt, niceStep, niceCeil } from "@/components/preview/useDragSvg";
import { C, Svg, handle, cross, pill, vDim } from "@/components/preview/canvasPrimitives";

const DEFAULTS = { centerFromFloor: "145", pictureHeight: "60", hangerFromTop: "5" };

const W = 880;
const H = 470;
const padL = 64;
const padR = 836;
const wallTopY = 24;
const floorY = 408;

export default function SinglePictureCalculator() {
  const [v, setV] = useState(DEFAULTS);
  const set = (k) => (val) => setV((s) => ({ ...s, [k]: val }));
  const setNow = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const num = (k) => {
    const x = parseFloat(String(v[k]).replace(",", "."));
    return isNaN(x) ? NaN : x;
  };

  const r = useMemo(() => calcSinglePicture(v), [v]);

  useEffect(() => {
    if (!r.ok) return;
    trackSettled("calculator_used", { calculator: "single_picture", mode: "default" });
    trackSettled("result_shown", { calculator: "single_picture", verdict: (r.warnings && r.warnings.length) ? "warn" : "ok" });
  }, [r]);

  const canvas = renderCanvas(v, r, num, setNow);
  const copyText = r.ok ? buildCopyText(r) : "";

  const fields = [
    { id: "centerFromFloor", label: "Bildmitte ab Boden", value: v.centerFromFloor, step: "1", onChange: set("centerFromFloor"), hint: "Übliche Augenhöhe liegt bei etwa 145 bis 150 cm." },
    { id: "pictureHeight", label: "Bildhöhe", value: v.pictureHeight, step: "0.5", onChange: set("pictureHeight") },
    { id: "hangerFromTop", label: "Aufhängung von Oberkante", value: v.hangerFromTop, step: "0.5", onChange: set("hangerFromTop"), hint: "So weit hängt die Öse oder Schnur unter der Bildoberkante." },
  ];

  return (
    <EditorShell
      testId="calc-single-picture"
      canvas={canvas}
      canvasReady={r.ok}
      emptyHint="Gib Bildmitte, Bildhöhe und Aufhängung ein, um die Vorschau zu sehen."
      toolbarHint="Ziehe das Bild nach oben oder unten, oder die Bohrmarke an der Aufhängung."
      onReset={() => setV(DEFAULTS)}
      fields={fields}
      warnings={r.ok ? r.warnings : []}
      hasResult={r.ok}
      results={r.ok ? buildResults(r) : []}
      steps={r.ok ? buildSteps(r) : []}
      copyText={copyText}
    />
  );
}

function renderCanvas(v, r, num, setNow) {
  const drawW = padR - padL;
  const cf = num("centerFromFloor") || 0;
  const ph = num("pictureHeight") || 0;
  const topEdge = cf + ph / 2;
  const viewCm = niceCeil(Math.max(topEdge + 22, 180));
  const scaleY = (floorY - wallTopY) / viewCm;
  const vy = (cm) => floorY - cm * scaleY;
  const yToCm = (px) => (floorY - px) / scaleY;
  const cxMid = (padL + padR) / 2;
  const picW = 168;
  const picX = cxMid - picW / 2;
  const startCF = num("centerFromFloor");
  const els = [];

  els.push(<rect key="wall" x={padL} y={wallTopY} width={drawW} height={floorY - wallTopY} fill={C.wall} stroke={C.wallStroke} strokeWidth={1.5} rx={4} />);
  const stepV = niceStep(viewCm);
  for (let h = 0; h <= viewCm + 0.1; h += stepV) {
    const y = vy(h);
    els.push(<line key={"lt" + h} x1={padL - 6} y1={y} x2={padL} y2={y} stroke={C.wallStroke} strokeWidth={1} />);
    els.push(
      <text key={"ll" + h} x={padL - 9} y={y + 4} fill={C.dim} fontSize={11} textAnchor="end" fontFamily="Inter,sans-serif" style={{ fontVariantNumeric: "tabular-nums" }}>
        {h}
      </text>
    );
  }
  els.push(<line key="floor" x1={padL} y1={floorY} x2={padR} y2={floorY} stroke={C.floor} strokeWidth={2.5} />);
  els.push(
    <text key="floorl" x={padR - 4} y={floorY + 18} fill={C.dim} fontSize={12} textAnchor="end" fontFamily="Inter,sans-serif">
      Boden
    </text>
  );

  if (r.ok) {
    const yTop = vy(r.topEdge);
    const pxH = ph * scaleY;
    const yCenter = vy(r.centerFromFloor);
    const drillY = vy(r.drillHeight);
    els.push(<rect key="pic" x={picX} y={yTop} width={picW} height={pxH} fill={C.object} stroke={C.objectStroke} strokeWidth={2} rx={3} />);
    els.push(<line key="cl" x1={picX - 60} y1={yCenter} x2={picX + picW + 60} y2={yCenter} stroke={C.center} strokeWidth={1.3} strokeDasharray="6 5" />);
    els.push(pill("cll", picX - 60, yCenter, "Bildmitte", C.center, "end", W));
    const dhx = (picX + picW + padR) / 2;
    els.push(<line key="dg" x1={cxMid} y1={drillY} x2={dhx} y2={drillY} stroke={C.guide} strokeWidth={1} strokeDasharray="3 4" />);
    els.push(vDim("vcf", floorY, yCenter, picX - 44, fmt(r.centerFromFloor) + " cm", C.dim, W));
    els.push(vDim("vph", yTop, vy(r.bottomEdge), picX + picW + 30, fmt(ph), C.dim, W));
    els.push(vDim("vdh", floorY, drillY, dhx, "Bohrhöhe " + fmt(r.drillHeight), C.primary, W));
    els.push(
      cross("cr", cxMid, drillY, (ev) =>
        startDrag(ev, W, H, (cur) => setNow("hangerFromTop", String(round1(clamp(r.topEdge - yToCm(cur.y), 0, ph)))))
      )
    );
    els.push(
      handle("hcf", picX, yCenter, "ns-resize", (ev) =>
        startDrag(ev, W, H, (cur, st) => setNow("centerFromFloor", String(round1(clamp(startCF - (cur.y - st.y) / scaleY, ph / 2, viewCm))))), "h")
    );
    els.push(
      handle("hph", cxMid, yTop, "ns-resize", (ev) =>
        startDrag(ev, W, H, (cur) => setNow("pictureHeight", String(round1(clamp(2 * (yToCm(cur.y) - cf), 5, 200))))), "h")
    );
  }

  return (
    <Svg vbW={W} vbH={H} ariaLabel="Vorschau: Einzelnes Bild auf Höhe bringen">
      {els}
    </Svg>
  );
}

function buildResults(r) {
  return [
    { label: "Bohrhöhe ab Boden", value: fmtCm(r.drillHeight) },
    { label: "Bildmitte ab Boden", value: fmtCm(r.centerFromFloor) },
    { label: "Bildoberkante", value: fmtCm(r.topEdge) },
    { label: "Bildunterkante", value: fmtCm(r.bottomEdge) },
  ];
}

function buildSteps(r) {
  return [
    `Bohre oder setze den Haken bei ${fmtCm(r.drillHeight)} Höhe ab dem Boden.`,
    `Die Bildmitte liegt danach bei ${fmtCm(r.centerFromFloor)}.`,
    "Prüfe die Markierung mit einer Wasserwaage, bevor du bohrst.",
    "Achte vor dem Bohren auf Leitungen und die Wandbeschaffenheit.",
  ];
}

function buildCopyText(r) {
  return [
    "Einzelnes Bild auf Höhe bringen",
    `Bohrhöhe ab Boden: ${fmtCm(r.drillHeight)}`,
    `Bildmitte ab Boden: ${fmtCm(r.centerFromFloor)}`,
    `Bildoberkante: ${fmtCm(r.topEdge)}, Bildunterkante: ${fmtCm(r.bottomEdge)}`,
  ].join("\n");
}
